import { Box, Typography, Button, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import themeColors from "../theme";

export default function PrivacyPolicyPage() {
    const navigate = useNavigate();
    const { teal } = themeColors;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", display: "flex", flexDirection: "column" }}>
            {/* Back Button */}
            <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0", backgroundColor: "white" }}>
                <Container maxWidth="lg">
                    <Button
                        variant="text"
                        onClick={() => navigate(-1)}
                        sx={{
                            color: teal,
                            fontSize: "1rem",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                        }}
                    >
                        ← Back
                    </Button>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ flexGrow: 1, py: 6 }}>
                <Box
                    sx={{
                        backgroundColor: "white",
                        borderRadius: 3,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        p: { xs: 4, md: 6 },
                    }}
                >
                    <Typography variant="h3" component="h1" sx={{ fontWeight: "bold", mb: 2 }}>
                        Privacy Policy
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#666", mb: 4 }}>
                        Last Updated: July 8, 2026
                    </Typography>

                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        OnCallCME ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
                        explains how we collect, use, disclose, and safeguard your information when you use our CME
                        management platform and services.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        1. Information We Collect
                    </Typography>

                    <Typography variant="h6" component="h3" sx={{ fontWeight: "600", mt: 3, mb: 1 }}>
                        Personal Information
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        We collect personal information that you voluntarily provide when registering for an account or
                        using our services, including:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Name and contact information (email address, phone number)</li>
                        <li>Professional credentials and licensing information</li>
                        <li>Medical specialty and practice information</li>
                        <li>CME credit and fund tracking data</li>
                        <li>Payment and billing information</li>
                    </Box>

                    <Typography variant="h6" component="h3" sx={{ fontWeight: "600", mt: 3, mb: 1 }}>
                        Usage Information
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        We automatically collect information about your device and how you interact with our platform:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Device information (IP address, browser type, operating system)</li>
                        <li>Log data (access times, pages viewed, links clicked)</li>
                        <li>Cookies and similar tracking technologies</li>
                    </Box>

                    <Typography variant="h6" component="h3" sx={{ fontWeight: "600", mt: 3, mb: 1 }}>
                        Documents and Files
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        When you use our vault feature, we store documents and files you upload, including certificates,
                        licenses, and other professional documents.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        2. How We Use Your Information
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        We use the information we collect to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li>Provide, maintain, and improve our services</li>
                        <li>Process transactions and manage your CME funds and credits</li>
                        <li>Send you updates about your account, CME requirements, and relevant educational opportunities</li>
                        <li>Respond to your requests and provide customer support</li>
                        <li>Analyze usage patterns to enhance user experience</li>
                        <li>Detect and prevent fraud, abuse, or security issues</li>
                        <li>Comply with legal obligations and professional standards</li>
                    </Box>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        3. Information Sharing and Disclosure
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        We do not sell your personal information. We may share your information only in the following circumstances:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf (payment processing, data hosting, analytics)</li>
                        <li><strong>CME Providers:</strong> When you purchase CME courses through our marketplace, we share necessary information with the course provider to facilitate enrollment</li>
                        <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government request</li>
                        <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred to the successor entity</li>
                        <li><strong>With Your Consent:</strong> We may share information for any other purpose disclosed to you with your consent</li>
                    </Box>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        4. Data Security
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        We implement appropriate technical and organizational security measures to protect your personal
                        information against unauthorized access, alteration, disclosure, or destruction. These measures include
                        encryption, secure socket layer technology (SSL), and regular security assessments. However, no method
                        of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        5. Data Retention
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        We retain your personal information for as long as necessary to fulfill the purposes outlined in this
                        Privacy Policy, unless a longer retention period is required by law. CME credit records may be retained
                        for extended periods to comply with professional certification and licensing requirements.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        6. Your Rights and Choices
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        Depending on your location, you may have certain rights regarding your personal information:
                    </Typography>
                    <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                        <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
                        <li><strong>Correction:</strong> You can update or correct inaccurate information through your account settings</li>
                        <li><strong>Deletion:</strong> You can request deletion of your account and associated data</li>
                        <li><strong>Opt-Out:</strong> You can opt-out of marketing communications by following the unsubscribe instructions in emails</li>
                        <li><strong>Data Portability:</strong> You can request a copy of your data in a portable format</li>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        To exercise these rights, please contact us using the information provided below.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        7. Cookies and Tracking Technologies
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns,
                        and remember your preferences. You can control cookie settings through your browser preferences, though
                        disabling cookies may limit certain features of our platform.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        8. Third-Party Links
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        Our platform may contain links to third-party websites and services, including CME course providers.
                        We are not responsible for the privacy practices of these third parties. We encourage you to review
                        their privacy policies before providing any personal information.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        9. Children's Privacy
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        Our services are intended for licensed healthcare professionals and are not directed to individuals
                        under the age of 18. We do not knowingly collect personal information from children.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        10. Changes to This Privacy Policy
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        We may update this Privacy Policy from time to time. We will notify you of any material changes by
                        posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use
                        of our services after such changes constitutes acceptance of the updated policy.
                    </Typography>

                    <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mt: 4, mb: 2 }}>
                        11. Contact Us
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                        If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                        <strong>OnCallCME</strong><br />
                        Email: Contact@oncallcme.com<br />
                        Address: 8401 Mayland Dr, Ste V, Richmond, VA 23294
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
